import { useEffect, useRef, useSyncExternalStore, type KeyboardEvent, type ReactNode } from 'react';
import {
  commitPending,
  dismissMutationFailure,
  getMutationFailure,
  getPendingUndo,
  holdPending,
  registerUndoHost,
  subscribeMutationFailure,
  subscribeUndo,
  undoPending,
} from '@/hooks/useMessageMutations';
import { createPortal } from 'react-dom';
import { useToastSlot } from './toastStack';
import './snackbar.css';

/**
 * A one-line notice with at most one action — Gmail's "Message archived ·
 * Undo" (plan 15 §6.1; doc 06 §3's send-undo can reuse it).
 *
 * ## Accessibility
 *
 * - The `role="status"` region is rendered *always*, empty when there is
 *   nothing to say. A live region inserted together with its text is often not
 *   announced; one that already exists and gains text is.
 * - It never takes focus. The person mid-triage keeps their place in the list
 *   and a screen-reader user hears the sentence without being moved.
 * - The action is an ordinary button in the tab order. While the pointer is
 *   over the snackbar or focus is inside it the owner is told to hold its
 *   clock (`onHoldChange`), so a keyboard or switch user is not raced by the
 *   timeout (WCAG 2.2.1). Escape dismisses.
 * - When the snackbar closes with focus inside it — the usual case after
 *   pressing Undo — focus goes back to where it was before it came in, rather
 *   than falling to `<body>` with the button that held it.
 */

export interface SnackbarProps {
  /** The sentence. `undefined` closes the snackbar. */
  message: string | undefined;
  /** Changes when the same sentence is said again, so it is announced again. */
  messageKey?: string | number;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Further actions after the first, each an ordinary button in the tab order
   * -- "Open in Nextcloud · Copy share link" (ADR 0086). An action with an
   * `href` is a link that opens in a new window.
   */
  moreActions?: ReadonlyArray<SnackbarAction>;
  /** Escape. */
  onDismiss?: () => void;
  /** True while hovered or focused within: the owner should stop its clock. */
  onHoldChange?: (held: boolean) => void;
  /**
   * Something beside the sentence that is not part of it — the send's
   * countdown. Rendered outside the text span so it does not change what the
   * live region announces; the caller hides it from assistive technology.
   */
  detail?: ReactNode;
  /**
   * An extra class on the region. Two snackbars can be on screen at once — a
   * queued send and a queued archive — and this is how the stylesheet tells
   * them apart to stack them (snackbar.css).
   */
  className?: string;
}

export interface SnackbarAction {
  label: string;
  onAction?: () => void;
  /** A link instead of a button, opened in a new window with no opener. */
  href?: string;
  disabled?: boolean;
}

export function Snackbar({
  message,
  messageKey,
  actionLabel,
  onAction,
  moreActions,
  onDismiss,
  onHoldChange,
  detail,
  className,
}: SnackbarProps) {
  const open = message !== undefined;
  const regionRef = useRef<HTMLDivElement>(null);
  /** The last element outside the snackbar that had focus: where focus returns. */
  const returnTo = useRef<Element | null>(null);
  const focusInside = useRef(false);
  const hovered = useRef(false);
  const held = useRef(false);

  const updateHold = () => {
    const next = focusInside.current || hovered.current;
    if (next === held.current) return;
    held.current = next;
    onHoldChange?.(next);
  };

  // While open, remember where focus is outside the snackbar.
  useEffect(() => {
    if (!open) return;
    const region = regionRef.current;
    const remember = (target: EventTarget | null) => {
      if (target instanceof Element && !region?.contains(target)) returnTo.current = target;
    };
    remember(document.activeElement);
    const onFocusIn = (event: FocusEvent) => remember(event.target);
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open]);

  // Closing: hand focus back if the snackbar had it, and forget the hold.
  useEffect(() => {
    if (open) return;
    hovered.current = false;
    held.current = false;
    if (!focusInside.current) return;
    focusInside.current = false;
    const target = returnTo.current;
    if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !onDismiss) return;
    event.preventDefault();
    // The mail keymap's Escape closes the composer; this one is only ours.
    event.stopPropagation();
    onDismiss();
  };

  return (
    <div
      ref={regionRef}
      className={className ? `snackbar ${className}` : 'snackbar'}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-open={open}
    >
      {open && (
        // The hold handlers are pointer/focus bookkeeping on a container, not
        // an interaction of their own; the button inside is the control.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className="snackbar__surface"
          onPointerEnter={() => {
            hovered.current = true;
            updateHold();
          }}
          onPointerLeave={() => {
            hovered.current = false;
            updateHold();
          }}
          onFocus={() => {
            focusInside.current = true;
            updateHold();
          }}
          onBlur={(event) => {
            if (regionRef.current?.contains(event.relatedTarget)) return;
            focusInside.current = false;
            updateHold();
          }}
          onKeyDown={onKeyDown}
        >
          <span key={messageKey} className="snackbar__text">
            {message}
          </span>
          {detail}
          {actionLabel && onAction && (
            <button type="button" className="btn btn--ghost snackbar__action" onClick={onAction}>
              {actionLabel}
            </button>
          )}
          {moreActions?.map((action) =>
            action.href !== undefined ? (
              <a
                key={action.label}
                className="btn btn--ghost snackbar__action"
                href={action.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={action.onAction}
              >
                {action.label}
              </a>
            ) : (
              <button
                key={action.label}
                type="button"
                className="btn btn--ghost snackbar__action"
                disabled={action.disabled}
                onClick={action.onAction}
              >
                {action.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The Undo snackbar for the undoable mail mutations, and the notice that
 * replaces it when one of them turns out to have failed.
 *
 * Mounting it is what makes archive, delete and move undoable at all: it
 * registers as the undo host, and with no host mounted those mutations commit
 * immediately (see "The undo window" in `useMessageMutations.ts`). Unmounting
 * the last one commits whatever is pending.
 *
 * The failure notice shares this one region rather than opening a second: it
 * is the correction to a sentence that was said here — "Message archived",
 * five seconds before the request went out — and it belongs where that was
 * said. It carries no action, because the rollback has already happened and
 * there is nothing left to press. A pending action outranks it: that Undo is
 * a live offer with a clock on it, and the notice has already had its say.
 */
export function UndoSnackbar() {
  const pending = useSyncExternalStore(subscribeUndo, getPendingUndo, getPendingUndo);
  const failure = useSyncExternalStore(
    subscribeMutationFailure,
    getMutationFailure,
    getMutationFailure,
  );

  useEffect(() => registerUndoHost(), []);
  // In the shell's toast stack when there is one (toastStack.ts), so it can
  // never sit under the send's snackbar or the update prompt.
  const slot = useToastSlot();

  const notice = pending ?? failure;
  const undoable = pending !== undefined;

  const snackbar = (
    <Snackbar
      className="snackbar--mail"
      message={notice?.label}
      // Prefixed: a failure that follows an undo could otherwise reuse the
      // pending entry's id and go unannounced.
      {...(notice ? { messageKey: `${undoable ? 'undo' : 'failed'}-${notice.id}` } : {})}
      {...(undoable
        ? {
            actionLabel: 'Undo',
            onAction: () => {
              undoPending();
            },
          }
        : {})}
      onDismiss={undoable ? commitPending : dismissMutationFailure}
      onHoldChange={holdPending}
    />
  );
  return slot ? createPortal(snackbar, slot) : snackbar;
}
